(function () {
  function rootPath() {
    return window.Shopify?.routes?.root || '/';
  }

  function formatMoney(cents) {
    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || '${{amount}}'
      );
    }

    return (cents / 100).toFixed(2);
  }

  function debounce(key, callback, delay) {
    if (!window.__cartDrawerDebounce) {
      window.__cartDrawerDebounce = {};
    }

    clearTimeout(window.__cartDrawerDebounce[key]);
    window.__cartDrawerDebounce[key] = setTimeout(callback, delay || 450);
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cartDrawerReady === 'true') return;
      this.dataset.cartDrawerReady = 'true';
      this.bindEvents();
    }

    bindEvents() {
      if (window.__cartDrawerListenersBound) return;
      window.__cartDrawerListenersBound = true;

      document.addEventListener('click', this.onDocumentClick.bind(this));
      document.addEventListener('keydown', this.onDocumentKeydown.bind(this));
      document.addEventListener('input', this.onDocumentInput.bind(this));
      document.addEventListener('change', this.onDocumentChange.bind(this));
    }

    get drawer() {
      return document.querySelector('cart-drawer');
    }

    get sectionId() {
      return this.drawer?.dataset.sectionId || 'cart-drawer';
    }

    onDocumentClick(event) {
      const drawer = this.drawer;
      if (!drawer) return;

      const addButton = event.target.closest('[data-add-to-cart]');
      if (addButton && !addButton.disabled) {
        event.preventDefault();
        event.stopPropagation();

        let quantity = 1;
        const quantitySelector = addButton.getAttribute('data-quantity-input');
        if (quantitySelector) {
          const quantityEl = document.querySelector(quantitySelector);
          if (quantityEl) {
            quantity = Math.max(1, Number.parseInt(quantityEl.value, 10) || 1);
          }
        }

        drawer.addToCart(addButton.dataset.variantId, addButton, quantity);
        return;
      }

      const openTrigger = event.target.closest('[data-cart-drawer-open]');
      if (openTrigger) {
        event.preventDefault();
        drawer.open();
        return;
      }

      if (!drawer.classList.contains('cart-drawer--open')) return;

      if (event.target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        drawer.close();
        return;
      }

      const decreaseButton = event.target.closest('[data-cart-qty-decrease]');
      if (decreaseButton) {
        event.preventDefault();
        drawer.adjustQuantity(decreaseButton.dataset.lineKey, -1);
        return;
      }

      const increaseButton = event.target.closest('[data-cart-qty-increase]');
      if (increaseButton) {
        event.preventDefault();
        drawer.adjustQuantity(increaseButton.dataset.lineKey, 1);
        return;
      }

      const removeButton = event.target.closest('[data-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        drawer.changeLineQuantity(removeButton.dataset.lineKey, 0);
      }
    }

    onDocumentKeydown(event) {
      const drawer = this.drawer;
      if (!drawer) return;

      if (event.key === 'Escape' && drawer.classList.contains('cart-drawer--open')) {
        drawer.close();
      }
    }

    onDocumentInput(event) {
      const drawer = this.drawer;
      if (!drawer) return;

      if (event.target.matches('[data-cart-note]')) {
        debounce('cart-note', () => drawer.updateNote(event.target.value));
      }
    }

    onDocumentChange(event) {
      const drawer = this.drawer;
      if (!drawer) return;

      const attributeField = event.target.closest('[data-cart-attribute]');
      if (attributeField) {
        drawer.updateAttribute(attributeField.dataset.cartAttribute, attributeField.value);
      }
    }

    async open(skipRefresh) {
      if (!skipRefresh) {
        await this.refreshDrawer();
      }

      this.classList.add('cart-drawer--open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');
      this.querySelector('.cart-drawer__close')?.focus();
    }

    close() {
      this.classList.remove('cart-drawer--open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-open');
      document.querySelector('[data-cart-drawer-open]')?.focus();
    }

    setLoading(isLoading) {
      this.classList.toggle('cart-drawer--loading', isLoading);
    }

    async fetchCart() {
      const response = await fetch(`${rootPath()}cart.js`);
      if (!response.ok) throw new Error('Failed to fetch cart');
      return response.json();
    }

    async fetchSectionHtml() {
      const sectionsUrl = encodeURIComponent(window.location.pathname || '/');
      const sectionId = encodeURIComponent(this.sectionId);
      const response = await fetch(
        `${rootPath()}?sections=${sectionId}&sections_url=${sectionsUrl}`
      );

      if (!response.ok) throw new Error('Failed to fetch section');

      const data = await response.json();
      return data[this.sectionId] || data[decodeURIComponent(sectionId)] || '';
    }

    preserveExtrasState() {
      const noteField = this.querySelector('[data-cart-note]');
      const attributeFields = this.querySelectorAll('[data-cart-attribute]');
      const openDetails = [...this.querySelectorAll('.cart-drawer__accordion[open]')].map(
        (_, index) => index
      );

      this._extrasState = {
        note: noteField?.value || '',
        attributes: {},
        openDetails,
      };

      attributeFields.forEach((field) => {
        this._extrasState.attributes[field.dataset.cartAttribute] = field.value;
      });
    }

    restoreExtrasState() {
      if (!this._extrasState) return;

      const noteField = this.querySelector('[data-cart-note]');
      if (noteField) noteField.value = this._extrasState.note;

      Object.entries(this._extrasState.attributes).forEach(([key, value]) => {
        const field = this.querySelector(`[data-cart-attribute="${key}"]`);
        if (field) field.value = value;
      });

      this.querySelectorAll('.cart-drawer__accordion').forEach((details, index) => {
        details.open = this._extrasState.openDetails.includes(index);
      });
    }

    sectionHasItems(html) {
      if (!html) return false;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return Boolean(doc.querySelector('[data-cart-drawer-items]'));
    }

    applySectionHtml(html) {
      if (!html) return false;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContents = doc.querySelector('[data-cart-drawer-contents]');
      const currentContents = this.querySelector('[data-cart-drawer-contents]');

      if (!newContents || !currentContents) return false;

      this.preserveExtrasState();
      currentContents.innerHTML = newContents.innerHTML;
      this.restoreExtrasState();
      return true;
    }

    updateTitleCount(itemCount) {
      const title = this.querySelector('.cart-drawer__title');
      if (!title) return;

      let countEl = title.querySelector('[data-cart-drawer-count]');

      if (itemCount > 0) {
        if (!countEl) {
          countEl = document.createElement('span');
          countEl.className = 'cart-drawer__count';
          countEl.dataset.cartDrawerCount = '';
          title.appendChild(countEl);
        }
        countEl.textContent = `(${itemCount})`;
      } else if (countEl) {
        countEl.remove();
      }
    }

    updateHeaderCount(itemCount) {
      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        if (itemCount > 0) {
          element.textContent = itemCount;
          element.hidden = false;
        } else {
          element.textContent = '';
          element.hidden = true;
        }
      });
    }

    renderCartFallback(cart) {
      const strings = window.theme?.cartDrawerStrings || {};
      const contents = this.querySelector('[data-cart-drawer-contents]');
      if (!contents) return;

      if (!cart.item_count) {
        contents.innerHTML = `
          <div class="cart-drawer__empty">
            <p class="cart-drawer__empty-text">${strings.empty || 'Your cart is empty'}</p>
            <a class="cart-drawer__empty-link" href="${strings.continueShoppingUrl || '/collections/all'}">
              ${strings.continueShopping || 'Continue shopping'}
            </a>
          </div>
        `;
        return;
      }

      const itemsHtml = cart.items
        .map(
          (item) => `
          <li class="cart-drawer__item" data-cart-item data-line-key="${item.key}">
            <a class="cart-drawer__item-media" href="${item.url}">
              ${
                item.image
                  ? `<img src="${item.image}" alt="${item.product_title}" class="cart-drawer__item-image" loading="lazy" width="80" height="100">`
                  : '<div class="cart-drawer__item-placeholder"></div>'
              }
            </a>
            <div class="cart-drawer__item-details">
              <a class="cart-drawer__item-title" href="${item.url}">${item.product_title}</a>
              ${
                item.variant_title && item.variant_title !== 'Default Title'
                  ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>`
                  : ''
              }
              <p class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</p>
              <div class="cart-drawer__item-actions">
                <div class="cart-drawer__quantity">
                  <button type="button" class="cart-drawer__quantity-button" data-cart-qty-decrease data-line-key="${item.key}" aria-label="${strings.decreaseQuantity || 'Decrease quantity'}">−</button>
                  <span class="cart-drawer__quantity-value" data-cart-qty-value>${item.quantity}</span>
                  <button type="button" class="cart-drawer__quantity-button" data-cart-qty-increase data-line-key="${item.key}" aria-label="${strings.increaseQuantity || 'Increase quantity'}">+</button>
                </div>
                <button type="button" class="cart-drawer__remove" data-cart-remove data-line-key="${item.key}">${strings.remove || 'Remove'}</button>
              </div>
            </div>
          </li>
        `
        )
        .join('');

      contents.innerHTML = `
        <div class="cart-drawer__scroll">
          <ul class="cart-drawer__items" data-cart-drawer-items role="list">${itemsHtml}</ul>
        </div>
        ${this.footerMarkup(cart)}
      `;
    }

    footerMarkup(cart) {
      const strings = window.theme?.cartDrawerStrings || {};

      return `
        <footer class="cart-drawer__footer" data-cart-drawer-footer>
          <div class="cart-drawer__summary">
            <div class="cart-drawer__subtotal-row">
              <span class="cart-drawer__subtotal-label">${strings.subtotal || 'Subtotal'}</span>
              <span class="cart-drawer__subtotal-value" data-cart-subtotal>${formatMoney(cart.total_price)}</span>
            </div>
          </div>
          <a href="/pages/checkout" class="cart-drawer__checkout cart-drawer__checkout-button">
            ${strings.continueToOrder || 'Continue to order'}
          </a>
        </footer>
      `;
    }

    async refreshDrawer() {
      let cart = null;

      try {
        cart = await this.fetchCart();
      } catch (error) {
        console.error(error);
        return;
      }

      this.updateHeaderCount(cart.item_count);
      this.updateTitleCount(cart.item_count);

      let usedSectionRender = false;

      try {
        const html = await this.fetchSectionHtml();
        const sectionMatchesCart =
          !cart.item_count || this.sectionHasItems(html);

        if (html && sectionMatchesCart && this.applySectionHtml(html)) {
          usedSectionRender = true;
        }
      } catch (error) {
        console.warn('Section render failed, using fallback', error);
      }

      if (!usedSectionRender) {
        this.renderCartFallback(cart);
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }

    async addToCart(variantId, button, quantity) {
      if (!variantId) return;

      const safeQuantity = Math.max(1, Number.parseInt(quantity, 10) || 1);

      button?.classList.add('product-card-premium__add--loading', 'is-loading');
      this.setLoading(true);

      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: Number.parseInt(variantId, 10), quantity: safeQuantity }],
          }),
        });

        if (!response.ok) throw new Error('Add to cart failed');

        await this.refreshDrawer();
        this.open(true);
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoading(false);
        button?.classList.remove('product-card-premium__add--loading', 'is-loading');
      }
    }

    adjustQuantity(lineKey, delta) {
      const safeKey = CSS.escape(lineKey);
      const quantityElement = this.querySelector(
        `[data-cart-item][data-line-key="${safeKey}"] [data-cart-qty-value]`
      );
      if (!quantityElement) return;

      const currentQuantity = Number.parseInt(quantityElement.textContent, 10) || 0;
      this.changeLineQuantity(lineKey, Math.max(0, currentQuantity + delta));
    }

    async changeLineQuantity(lineKey, quantity) {
      this.setLoading(true);

      try {
        const response = await fetch(`${rootPath()}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: lineKey, quantity }),
        });

        if (!response.ok) throw new Error('Cart update failed');

        await this.refreshDrawer();
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoading(false);
      }
    }

    async updateNote(note) {
      try {
        await fetch(`${rootPath()}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ note }),
        });
      } catch (error) {
        console.error(error);
      }
    }

    async updateAttribute(key, value) {
      try {
        await fetch(`${rootPath()}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ attributes: { [key]: value } }),
        });
      } catch (error) {
        console.error(error);
      }
    }
  }

  function initCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    if (!customElements.get('cart-drawer')) {
      customElements.define('cart-drawer', CartDrawer);
    }

    if (drawer.dataset.cartDrawerReady !== 'true') {
      drawer.dataset.cartDrawerReady = 'true';
      CartDrawer.prototype.bindEvents.call(drawer);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawer);
  } else {
    initCartDrawer();
  }
})();
